import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, RefreshCw, AlertCircle, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/apple-design-system.css';
import ThemeToggle from '@/components/apple-ui/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPasswordPageApple = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  // Pre-fill email if redirected from login (needs_password)
  const initialEmail = location.state?.email || '';
  const needsPassword = location.state?.needsPassword || false;
  const mustChange = location.state?.mustChange || false;

  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'password' | 'done'
  const [email, setEmail] = useState(initialEmail);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({ new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const otpRefs = useRef([]);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  // Auto-send OTP if redirected from login with needs_password
  // Skip OTP for invited users who must change password
  useEffect(() => {
    if (initialEmail && mustChange) {
      // Invited user - skip OTP, go directly to password step
      setStep('password');
    } else if (initialEmail && needsPassword) {
      sendOTP(initialEmail);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOTP = async (targetEmail) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail || email, purpose: 'forgot_password' })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('OTP sent to your email');
        setStep('otp');
        startCooldown();
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email'); return; }
    await sendOTP(email);
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setError('');
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (value && index === 5 && newOtp.every(d => d)) setStep('password');
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      setOtpCode(pasted.split(''));
      otpRefs.current[5]?.focus();
      setTimeout(() => setStep('password'), 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      // For invited users (mustChange), use JWT-based endpoint instead of OTP
      const authToken = localStorage.getItem('token');
      const useInvitedEndpoint = mustChange && authToken;
      
      const response = await fetch(
        useInvitedEndpoint 
          ? `${API_URL}/api/auth/set-invited-password`
          : `${API_URL}/api/auth/verify-otp`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(useInvitedEndpoint ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify(
            useInvitedEndpoint
              ? { new_password: newPassword }
              : { email, otp_code: otpCode.join(''), purpose: 'forgot_password', new_password: newPassword }
          )
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success('Password set successfully!');
        // Check for pending invitation token
        const pendingToken = localStorage.getItem('pending_invitation_token');
        if (mustChange && pendingToken) {
          setTimeout(() => { window.location.href = `/accept-invitation?token=${pendingToken}`; }, 1000);
        } else if (mustChange) {
          // Re-login to get fresh token then go to dashboard
          setTimeout(() => navigate('/login'), 1000);
        } else {
          setStep('done');
        }
      } else {
        setError(data.detail || 'Failed to set password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', fontSize: '16px', borderRadius: '12px',
    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}`,
    background: isDarkMode ? '#2c2c2e' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1d1d1f',
    outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '500',
    color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f', marginBottom: '6px'
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', justifyContent: 'center', alignItems: 'center', background: isDarkMode ? '#000000' : '#fff', padding: '20px' }}
      data-testid="forgot-password-page">
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}><ThemeToggle /></div>

      <div style={{ width: '100%', maxWidth: '400px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '44px', height: '44px', background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginBottom: '12px', boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
            <img src="/logo-marketing.png" alt="t09n.com" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: 0, display: 'block' }} />
          </div>
          <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: '18px', color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>
            t09n<span style={{ fontWeight: 700 }}>.com</span>
          </span>
          <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: '3px', fontSize: '11px', color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px', marginBottom: '20px' }}>
            The Operating System For Translators
          </span>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: isDarkMode ? '#fff' : '#1d1d1f', margin: '0 0 4px' }}>
            {needsPassword ? 'Set Your Password' : 'Reset Password'}
          </h2>
          <p style={{ fontSize: '14px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b', margin: 0 }}>
            {needsPassword ? 'Your account needs a password to continue' : 'We\'ll send a code to your email'}
          </p>
        </div>

        <div style={{
          background: isDarkMode ? '#25262b' : '#f5f5f7', borderRadius: '20px', padding: '32px',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
        }}>
          {error && (
            <div style={{ background: isDarkMode ? 'rgba(255,59,48,0.15)' : '#FEE2E2', color: '#FF3B30', padding: '12px 16px', borderRadius: '12px', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}
              data-testid="forgot-error-message">
              <AlertCircle size={18} />{error}
            </div>
          )}

          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleSendOTP} data-testid="forgot-email-form">
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Email address</label>
                <input type="email" placeholder="you@company.com" value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required style={inputStyle} data-testid="forgot-email-input"
                  onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                  onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-send-otp-btn"
                style={{ width: '100%', padding: '16px', fontSize: '17px', fontWeight: '600', borderRadius: '12px', border: 'none', background: '#007AFF', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {loading ? <><Loader2 size={18} className="animate-spin" />Sending OTP...</> : <><Mail size={18} />Send OTP</>}
              </button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 'otp' && (
            <>
              <button type="button" onClick={() => { setStep('email'); setOtpCode(['','','','','','']); setError(''); }}
                style={{ background: 'none', border: 'none', color: isDarkMode ? '#6B9AFF' : '#0066CC', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', padding: 0 }}
                data-testid="forgot-back-btn">
                <ArrowLeft size={16} />Back
              </button>
              <p style={{ fontSize: '14px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b', marginBottom: '24px' }}>
                Enter the 6-digit code sent to <strong style={{ color: isDarkMode ? '#fff' : '#000' }}>{email}</strong>
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
                {otpCode.map((digit, index) => (
                  <input key={index} ref={el => otpRefs.current[index] = el} type="text" inputMode="numeric" maxLength={1}
                    value={digit} onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)} onPaste={index === 0 ? handleOtpPaste : undefined}
                    style={{ width: '46px', height: '54px', fontSize: '24px', fontWeight: '600', textAlign: 'center', borderRadius: '12px', border: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#d1d1d6'}`, background: isDarkMode ? '#2c2c2e' : '#fff', color: isDarkMode ? '#fff' : '#000', outline: 'none' }}
                    onFocus={(e) => e.target.style.borderColor = '#007AFF'}
                    onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.2)' : '#d1d1d6'}
                    data-testid={`forgot-otp-input-${index}`}
                  />
                ))}
              </div>
              <div style={{ textAlign: 'center' }}>
                <button type="button" onClick={() => sendOTP(email)} disabled={resendCooldown > 0 || loading}
                  style={{ background: 'none', border: 'none', color: resendCooldown > 0 ? '#999' : (isDarkMode ? '#6B9AFF' : '#0066CC'), fontSize: '14px', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  data-testid="forgot-resend-otp-btn">
                  <RefreshCw size={14} />{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't receive code? Resend"}
                </button>
              </div>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <form onSubmit={handleSetPassword} data-testid="forgot-password-form">
              <button type="button" onClick={() => { setStep('otp'); setError(''); }}
                style={{ background: 'none', border: 'none', color: isDarkMode ? '#6B9AFF' : '#0066CC', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', padding: 0 }}>
                <ArrowLeft size={16} />Back to OTP
              </button>
              <p style={{ fontSize: '14px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b', marginBottom: '20px' }}>
                Choose a strong password for your account.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>New password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPasswords.new ? 'text' : 'password'} placeholder="Min 8 chars, upper, lower, number, symbol"
                    value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    required style={{ ...inputStyle, paddingRight: '42px' }} data-testid="forgot-new-password-input"
                    onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                    onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                  />
                  <button type="button" onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#86868b', padding: 0 }}>
                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Confirm password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPasswords.confirm ? 'text' : 'password'} placeholder="Re-enter password"
                    value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    required style={{ ...inputStyle, paddingRight: '42px' }} data-testid="forgot-confirm-password-input"
                    onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                    onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                  />
                  <button type="button" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#86868b', padding: 0 }}>
                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-set-password-btn"
                style={{ width: '100%', padding: '16px', fontSize: '17px', fontWeight: '600', borderRadius: '12px', border: 'none', background: '#007AFF', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {loading ? <><Loader2 size={18} className="animate-spin" />Setting password...</> : <><Lock size={18} />Set New Password</>}
              </button>
            </form>
          )}

          {/* Step 4: Done */}
          {step === 'done' && (
            <div style={{ textAlign: 'center' }} data-testid="forgot-success">
              <CheckCircle size={48} style={{ color: '#34C759', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: isDarkMode ? '#fff' : '#1d1d1f', marginBottom: '8px' }}>
                Password Set Successfully
              </h3>
              <p style={{ fontSize: '14px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b', marginBottom: '24px' }}>
                You can now sign in with your email and new password.
              </p>
              <button onClick={() => navigate('/login')} data-testid="forgot-goto-login-btn"
                style={{ width: '100%', padding: '16px', fontSize: '17px', fontWeight: '600', borderRadius: '12px', border: 'none', background: '#007AFF', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                Go to Login
              </button>
            </div>
          )}

          {/* Footer */}
          {step !== 'done' && (
            <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b' }}>
              Remember your password?{' '}
              <Link to="/login" style={{ color: '#007AFF', textDecoration: 'none', fontWeight: '500' }}>Sign in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export { ForgotPasswordPageApple };
export default ForgotPasswordPageApple;
